import React, { useEffect, useRef } from 'react';
import { Badge, Icon } from '../ui';
import type { BadgeTone } from '../ui';
import type { HostingRunStep } from '../../lib/hosting';
import { bytes, dateTime, day, host } from './runFormat';

// The runner writes `detail`; every field may be missing or the wrong type, so
// everything below reads through these and renders what it finds.
type Obj = Record<string, unknown>;
const obj = (v: unknown): Obj | null => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : null);
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : typeof v === 'number' ? String(v) : null);
const num = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : typeof v === 'string' && v.trim() && Number.isFinite(Number(v)) ? Number(v) : null;
const bool = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null);
const words = (s: string) => s.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
const money = (n: number | null) => (n === null ? null : n.toLocaleString(undefined, { style: 'currency', currency: 'USD' }));

/** The expanded body of one checklist step, by step type. */
export default function StepDetail({ step }: { step: HostingRunStep }) {
  const d = obj(step.detail) ?? {};
  switch (step.step) {
    case 'payment':
      return <PaymentDetail d={d} />;
    case 'uptime':
      return <UptimeDetail d={d} />;
    case 'snapshot':
      return <SnapshotDetail d={d} />;
    case 'updates':
      return <UpdatesDetail d={d} />;
    case 'render':
      return <RenderDetail d={d} />;
    case 'smtp':
      return <SmtpDetail d={d} />;
    case 'security':
      return <SecurityDetail d={d} />;
    case 'lighthouse':
      return <LighthouseDetail d={d} />;
    case 'deploy':
      return <DeployDetail d={d} />;
    case 'build':
      return <BuildDetail d={d} />;
    case 'audit':
      return <AuditDetail d={d} />;
    case 'versions':
      return <VersionsDetail d={d} />;
    case 'secrets':
      return <SecretsDetail d={d} />;
    case 'headers':
      return <HeadersDetail d={d} />;
    default:
      return <RawDetail d={d} />;
  }
}

// ───────────────────────── Steps ─────────────────────────

function PaymentDetail({ d }: { d: Obj }) {
  const status = str(d.payment_status);
  const tone: BadgeTone =
    status === 'current' ? 'up' : status === 'past_due' || status === 'unpaid' ? 'down' : status === 'failing' ? 'warn' : 'neutral';
  const amount = money(num(d.last_payment_amount));
  const at = str(d.last_payment_at);
  return (
    <Facts
      rows={[
        ['Payment', status ? <Badge tone={tone}>{words(status)}</Badge> : null],
        ['Last payment', amount || at ? [amount, at ? dateTime(at) : null].filter(Boolean).join(' · ') : null],
        ['Subscription', str(d.subscription_status) ? words(str(d.subscription_status) as string) : null],
        ['Note', str(d.billing_note)],
      ]}
    />
  );
}

function UptimeDetail({ d }: { d: Obj }) {
  const sites = arr(d.sites).map(obj).filter(Boolean) as Obj[];
  const days = num(d.days);
  if (!sites.length) return <RawDetail d={d} />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {days ? <span className="t-eyebrow">Last {days} days</span> : null}
      <Rows>
        {sites.map((s, i) => {
          const avail = num(s.availability);
          const down = num(s.down_checks);
          const checks = num(s.checks);
          const color = avail === null ? 'var(--text-faint)' : avail >= 99.9 ? 'var(--status-up)' : avail >= 99 ? 'var(--status-warn)' : 'var(--status-down)';
          return (
            <Row key={i}>
              <span style={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-body)' }}>
                {str(s.name) || (str(s.url) ? host(str(s.url) as string) : 'Site')}
              </span>
              {checks !== null ? (
                <span className="t-num" style={{ color: down ? 'var(--status-warn)' : 'var(--text-faint)', fontSize: 'var(--text-xs)' }}>
                  {down ?? 0} down / {checks.toLocaleString()} checks
                </span>
              ) : null}
              <span className="t-num" style={{ color, minWidth: '64px', textAlign: 'right' }}>
                {avail === null ? '—' : `${avail >= 99.995 ? '100' : avail.toFixed(2)}%`}
              </span>
            </Row>
          );
        })}
      </Rows>
    </div>
  );
}

function SnapshotDetail({ d }: { d: Obj }) {
  const file = (name: unknown, size: unknown) =>
    str(name) || bytes(size) ? (
      <span>
        <span className="t-num" style={{ overflowWrap: 'anywhere' }}>{str(name) || '—'}</span>
        {bytes(size) ? <span style={{ color: 'var(--text-faint)' }}> · {bytes(size)}</span> : null}
      </span>
    ) : null;
  return (
    <Facts
      rows={[
        ['Database', file(d.db_file, d.db_bytes)],
        ['Files', file(d.files_file, d.files_bytes)],
      ]}
    />
  );
}

function UpdatesDetail({ d }: { d: Obj }) {
  const applied = bool(d.applied);
  const core = obj(d.core);
  const plugins = arr(d.plugins).map(obj).filter(Boolean) as Obj[];
  const themes = arr(d.themes).map(obj).filter(Boolean) as Obj[];
  const pending = arr(d.pending_after).map(str).filter(Boolean) as string[];
  const passes = num(d.passes);
  const nothing = !core && !plugins.length && !themes.length;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
        {applied !== null ? <Badge tone={applied ? 'up' : 'neutral'}>{applied ? 'Applied' : 'Listed only'}</Badge> : null}
        {[
          core ? (
            <span key="core">
              WordPress <FromTo from={core.from} to={core.to} />
            </span>
          ) : null,
          nothing ? <span key="none">Everything was up to date</span> : null,
          passes !== null ? <span key="passes">{passes} pass{passes === 1 ? '' : 'es'}</span> : null,
        ]
          .filter(Boolean)
          .map((part, i) => (
            <React.Fragment key={i}>
              {i ? <span style={{ color: 'var(--text-faint)' }}>·</span> : null}
              {part}
            </React.Fragment>
          ))}
      </div>
      {plugins.length ? <UpdateList label="Plugins" items={plugins} /> : null}
      {themes.length ? <UpdateList label="Themes" items={themes} /> : null}
      {pending.length ? (
        <Note tone="warn">
          Still pending after the run: <span className="t-num">{pending.join(', ')}</span>
        </Note>
      ) : null}
    </div>
  );
}

const UPDATE_TONE: Record<string, BadgeTone> = { updated: 'up', failed: 'down', pending: 'warn' };

function UpdateList({ label, items }: { label: string; items: Obj[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <span className="t-eyebrow">
        {label} · {items.length}
      </span>
      <Rows>
        {items.map((p, i) => {
          const status = str(p.status);
          const error = str(p.error);
          return (
            <div key={i} style={{ padding: '8px 0', borderTop: i ? '1px solid var(--border-hairline)' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <span style={{ flex: '1 1 160px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-body)' }} title={str(p.name) ?? undefined}>
                  {str(p.title) || str(p.name) || 'Unnamed'}
                </span>
                {/* Versions and status wrap as one unit, so a phone gets title / versions + status. */}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', marginLeft: 'auto' }}>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                    <FromTo from={p.from} to={p.to} />
                  </span>
                  {status ? <Badge tone={UPDATE_TONE[status] ?? 'neutral'}>{words(status)}</Badge> : null}
                </span>
              </div>
              {error ? <div style={{ marginTop: '4px', fontSize: 'var(--text-xs)', color: 'var(--status-down)', overflowWrap: 'anywhere' }}>{error}</div> : null}
            </div>
          );
        })}
      </Rows>
    </div>
  );
}

function RenderDetail({ d }: { d: Obj }) {
  const pages = arr(d.pages).map(obj).filter(Boolean) as Obj[];
  const purged = bool(d.purged_cache);
  // Netlify runs deploy nothing, so they compare against the previous run's shots.
  const comparedAt = str(d.compared_to_run_at);
  if (!pages.length && purged === null) return <RawDetail d={d} />;
  const captions: Captions = comparedAt ? { before: 'Previous run', beforeSub: `(${shortDay(comparedAt)})`, after: 'Now' } : { before: 'Before', after: 'After' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {purged !== null || comparedAt ? (
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
          {[
            comparedAt ? `Compared with the run on ${dateTime(comparedAt)}.` : null,
            purged === null ? null : purged ? 'Cache purged before the after shots.' : 'Cache was not purged.',
          ]
            .filter(Boolean)
            .join(' ')}
        </span>
      ) : null}
      {pages.map((p, i) => (
        <RenderPage key={i} p={p} captions={captions} />
      ))}
    </div>
  );
}

type Captions = { before: string; beforeSub?: string; after: string };

function RenderPage({ p, captions }: { p: Obj; captions: Captions }) {
  const url = str(p.url);
  const code = num(p.status_code);
  const consoleErrors = num(p.console_errors);
  const diff = num(p.diff_pct);
  const failedCss = arr(p.failed_css).map(str).filter(Boolean) as string[];
  const brokenImages = arr(p.broken_images).map(str).filter(Boolean) as string[];
  const overflow = num(p.mobile_overflow_px);
  const blank = bool(p.blank);
  const before = obj(p.before);
  const after = obj(p.after);
  const changed = diff !== null && diff >= 10;
  const shots = (['desktop', 'mobile'] as const).filter((k) => str(before?.[k]) || str(after?.[k]));
  // Netlify runs check the page as it is now; only WordPress update runs have a before.
  const pair = Boolean(before);
  const problem = changed || failedCss.length || brokenImages.length || blank || (overflow !== null && overflow >= 20);
  return (
    <div
      style={{
        padding: '12px',
        borderRadius: 'var(--radius-md)',
        border: `1px solid ${problem ? 'color-mix(in srgb, var(--status-warn) 45%, transparent)' : 'var(--border-hairline)'}`,
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <span style={{ flex: '1 1 180px', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <span style={{ color: 'var(--text-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{str(p.label) || (url ? host(url) : 'Page')}</span>
          {url ? (
            <a href={url} target="_blank" rel="noopener noreferrer" className="hover-underline" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {host(url)}
            </a>
          ) : null}
        </span>
        {code !== null ? <Badge tone={code >= 200 && code < 400 ? 'up' : 'down'}>{code}</Badge> : null}
        {consoleErrors !== null ? (
          <Badge tone={consoleErrors ? 'warn' : 'neutral'}>
            {consoleErrors} console error{consoleErrors === 1 ? '' : 's'}
          </Badge>
        ) : null}
        {diff !== null ? <Badge tone={changed ? 'warn' : 'neutral'}>{`${diff < 10 ? diff.toFixed(1) : Math.round(diff)}% diff`}</Badge> : null}
      </div>
      {failedCss.length ? (
        <Note tone="down">
          {failedCss.length} stylesheet{failedCss.length === 1 ? '' : 's'} failed to load:
          {failedCss.map((c) => (
            <span key={c} className="t-num" style={{ display: 'block', overflowWrap: 'anywhere' }}>
              {c}
            </span>
          ))}
        </Note>
      ) : null}
      {brokenImages.length ? (
        <Note tone="down">
          {brokenImages.length} image{brokenImages.length === 1 ? '' : 's'} failed to load:
          {brokenImages.map((c) => (
            <span key={c} className="t-num" style={{ display: 'block', overflowWrap: 'anywhere' }}>
              {c}
            </span>
          ))}
        </Note>
      ) : null}
      {blank ? <Note tone="down">The page loaded but shows almost no text.</Note> : null}
      {overflow !== null && overflow >= 20 ? <Note tone="warn">On a phone the page is {overflow}px wider than the screen, so it scrolls sideways.</Note> : null}
      {shots.length ? (
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          {shots.map((k) => (
            // The desktop pair shrinks on a phone; the mobile pair is narrow already.
            <div key={k} style={{ display: 'flex', flexDirection: 'column', gap: '6px', ...(k === 'desktop' ? { flex: '0 1 280px', minWidth: 0 } : {}) }}>
              <span className="t-eyebrow">{k === 'desktop' ? 'Desktop' : 'Mobile'}</span>
              <div style={k === 'desktop' ? { display: 'grid', gridTemplateColumns: `repeat(${pair ? 2 : 1}, minmax(0, 136px))`, gap: '8px' } : { display: 'flex', gap: '8px' }}>
                {pair ? <Thumb src={str(before?.[k])} caption={captions.before} sub={captions.beforeSub} mobile={k === 'mobile'} /> : null}
                <Thumb src={str(after?.[k])} caption={pair ? captions.after : 'Now'} mobile={k === 'mobile'} highlight={changed} />
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function Thumb({ src, caption, sub, mobile, highlight }: { src: string | null; caption: string; sub?: string; mobile: boolean; highlight?: boolean }) {
  const box: React.CSSProperties = {
    display: 'block',
    ...(mobile ? { width: '50px', height: '86px' } : { width: '100%', aspectRatio: '136 / 86' }),
    borderRadius: 'var(--radius-xs)',
    border: `1px solid ${highlight ? 'var(--status-warn)' : 'var(--border-hairline)'}`,
    background: 'var(--surface-raised)',
    overflow: 'hidden',
  };
  return (
    <figure style={{ margin: 0, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {src ? (
        <a href={src} target="_blank" rel="noopener noreferrer" className="hover-lift" style={box} title={`${caption}${sub ? ` ${sub}` : ''} · open full size`}>
          <img src={src} alt={caption} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }} />
        </a>
      ) : (
        <span style={{ ...box, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--text-2xs)', color: 'var(--text-faint)' }}>None</span>
      )}
      <figcaption style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-faint)', whiteSpace: 'nowrap' }}>
        {caption}
        {/* The date goes on its own line so a 50px mobile shot keeps its neighbour close. */}
        {sub ? <span style={{ display: 'block' }}>{sub}</span> : null}
      </figcaption>
    </figure>
  );
}

const AUTH_TONE: Record<string, BadgeTone> = { pass: 'up', fail: 'down', softfail: 'warn', neutral: 'warn', none: 'warn', temperror: 'warn', permerror: 'down' };

function SmtpDetail({ d }: { d: Obj }) {
  const sent = bool(d.sent);
  const sendError = str(d.send_error);
  const received = str(d.received_at);
  const auth = obj(d.auth);
  return (
    <Facts
      rows={[
        ['To', str(d.to)],
        ['Subject', str(d.subject)],
        ['Sent', sent === null ? null : sent ? 'Yes' : <span style={{ color: 'var(--status-down)' }}>No{sendError ? ` · ${sendError}` : ''}</span>],
        ['Received', received ? dateTime(received) : sent ? <span style={{ color: 'var(--status-warn)' }}>Not received</span> : null],
        [
          'Auth',
          auth ? (
            <span style={{ display: 'inline-flex', gap: '6px', flexWrap: 'wrap' }}>
              {(['spf', 'dkim', 'dmarc'] as const).map((k) => {
                const v = str(auth[k])?.toLowerCase() ?? null;
                return (
                  <Badge key={k} tone={v ? AUTH_TONE[v] ?? 'neutral' : 'neutral'}>
                    {k} {v ?? '—'}
                  </Badge>
                );
              })}
            </span>
          ) : null,
        ],
        ['Mailer', str(d.mailer)],
      ]}
    />
  );
}

function severity(n: number | null): { label: string; tone: BadgeTone; rank: number } {
  if (n === null) return { label: 'Unknown', tone: 'neutral', rank: 0 };
  if (n >= 100) return { label: 'Critical', tone: 'down', rank: 4 };
  if (n >= 75) return { label: 'High', tone: 'down', rank: 3 };
  if (n >= 50) return { label: 'Medium', tone: 'warn', rank: 2 };
  return { label: 'Low', tone: 'neutral', rank: 1 };
}

function SecurityDetail({ d }: { d: Obj }) {
  const active = bool(d.wordfence_active);
  const scanAt = str(d.last_scan_at);
  const scanStatus = str(d.last_scan_status);
  const issues = (arr(d.issues).map(obj).filter(Boolean) as Obj[])
    .map((i) => ({ msg: str(i.shortMsg), type: str(i.type), sev: severity(num(i.severity)) }))
    .sort((a, b) => b.sev.rank - a.sev.rank);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <Facts
        rows={[
          ['Wordfence', active === null ? null : <Badge tone={active ? 'up' : 'down'}>{active ? 'Active' : 'Inactive'}</Badge>],
          ['Last scan', scanAt || scanStatus ? [scanAt ? dateTime(scanAt) : null, scanStatus ? words(scanStatus) : null].filter(Boolean).join(' · ') : null],
          ['Issues', Array.isArray(d.issues) && !issues.length ? 'None open' : null],
        ]}
      />
      {issues.length ? (
        <Rows>
          {issues.map((i, n) => (
            <Row key={n} align="flex-start">
              <span style={{ width: '72px', flexShrink: 0 }}>
                <Badge tone={i.sev.tone}>{i.sev.label}</Badge>
              </span>
              <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere', color: 'var(--text-body)' }}>
                {i.msg || 'Issue'}
                {i.type ? <span style={{ display: 'block', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>{words(i.type)}</span> : null}
              </span>
            </Row>
          ))}
        </Rows>
      ) : null}
    </div>
  );
}

const SCORES: Array<[string, string]> = [
  ['performance', 'Performance'],
  ['accessibility', 'Accessibility'],
  ['best_practices', 'Best practices'],
  ['seo', 'SEO'],
];

function LighthouseDetail({ d }: { d: Obj }) {
  const scores = obj(d.scores) ?? {};
  const url = str(d.url);
  const strategy = str(d.strategy);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {url || strategy ? (
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', overflowWrap: 'anywhere' }}>
          {[url ? host(url) : null, strategy ? words(strategy) : null].filter(Boolean).join(' · ')}
        </span>
      ) : null}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(118px, 1fr))', gap: '8px' }}>
        {SCORES.map(([key, label]) => {
          const v = num(scores[key]);
          const color = v === null ? 'var(--text-faint)' : v < 50 ? 'var(--status-down)' : v < 90 ? 'var(--status-warn)' : 'var(--status-up)';
          const bg = v === null ? 'var(--surface-raised)' : v < 50 ? 'var(--status-down-soft)' : v < 90 ? 'var(--status-warn-soft)' : 'var(--status-up-soft)';
          return (
            <div key={key} style={{ padding: '8px 10px', borderRadius: 'var(--radius-md)', background: bg, display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span className="t-num" style={{ fontSize: 'var(--text-xl)', fontWeight: 600, lineHeight: 'var(--leading-tight)', color }}>
                {v === null ? '—' : Math.round(v)}
              </span>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ───────────────────────── Netlify steps ─────────────────────────

const DEPLOY_TONE: Record<string, BadgeTone> = { ready: 'up', error: 'down', building: 'info', enqueued: 'info', processing: 'info', uploading: 'info', preparing: 'info' };

function DeployDetail({ d }: { d: Obj }) {
  const adminUrl = str(d.admin_url);
  const publishedAt = str(d.published_at);
  const commit = str(d.published_commit);
  const commitUrl = str(d.published_commit_url);
  const deployUrl = str(d.deploy_url);
  const repo = str(d.repo);
  const branch = str(d.branch);
  const locked = bool(d.locked);
  const failedSince = num(d.failed_since_published);
  const recent = arr(d.recent).map(obj).filter(Boolean) as Obj[];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <Facts
        rows={[
          [
            'Site',
            str(d.site_name) || adminUrl ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px 10px', flexWrap: 'wrap' }}>
                {str(d.site_name) ? <span className="t-num">{str(d.site_name)}</span> : null}
                {adminUrl ? <ExtLink href={adminUrl}>Open in Netlify</ExtLink> : null}
              </span>
            ) : null,
          ],
          [
            'Published',
            publishedAt || commit ? (
              <span>
                {publishedAt ? dateTime(publishedAt) : null}
                {publishedAt && commit ? <span style={{ color: 'var(--text-faint)' }}> · </span> : null}
                {commit ? <Commit sha={commit} url={commitUrl} /> : null}
              </span>
            ) : null,
          ],
          [
            'Repository',
            repo || branch ? (
              <span className="t-num" style={{ display: 'inline-flex', alignItems: 'center', gap: '2px 8px', flexWrap: 'wrap' }}>
                {repo ? <span>{repo.replace(/^https?:\/\/(www\.)?github\.com\//i, '')}</span> : null}
                {branch ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', color: 'var(--text-muted)' }}>
                    <Icon name="git-branch" size={12} style={{ color: 'var(--text-faint)' }} />
                    {branch}
                  </span>
                ) : null}
              </span>
            ) : null,
          ],
          ['Live deploy', deployUrl ? <ExtLink href={deployUrl}>{host(deployUrl)}</ExtLink> : null],
        ]}
      />
      {locked ? (
        <Note tone="warn">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <Icon name="lock" size={12} style={{ flexShrink: 0 }} />
            Publishing is locked: new deploys build but none goes live until it is unlocked in Netlify.
          </span>
        </Note>
      ) : null}
      {failedSince ? (
        <Note tone="down">
          {failedSince} deploy{failedSince === 1 ? '' : 's'} failed since the published one.
        </Note>
      ) : null}
      {recent.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <span className="t-eyebrow">Recent deploys · {recent.length}</span>
          <Rows>
            {recent.map((r, i) => {
              const state = str(r.state);
              const error = str(r.error_message);
              const ref = str(r.commit_ref);
              const where = [str(r.context) ? words(str(r.context) as string) : null, str(r.branch)].filter(Boolean).join(' · ');
              return (
                <Row key={str(r.id) ?? i} align="flex-start">
                  <span style={{ width: '76px', flexShrink: 0 }}>
                    <Badge tone={state ? DEPLOY_TONE[state] ?? 'neutral' : 'neutral'}>{state ? words(state) : '—'}</Badge>
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    {/* The time sits right on a wide card and drops under the branch on a phone. */}
                    <span style={{ display: 'flex', alignItems: 'baseline', gap: '0 10px', flexWrap: 'wrap', minHeight: '20px' }}>
                      <span style={{ flex: '1 1 150px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-body)', lineHeight: '20px' }}>
                        {where || 'Deploy'}
                        {ref ? <span className="t-num" style={{ color: 'var(--text-faint)', fontSize: 'var(--text-xs)' }}> · {ref.slice(0, 7)}</span> : null}
                      </span>
                      {str(r.created_at) ? (
                        <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', whiteSpace: 'nowrap' }}>
                          {dateTime(str(r.created_at))}
                        </span>
                      ) : null}
                    </span>
                    {error ? <span style={{ display: 'block', marginTop: '2px', fontSize: 'var(--text-xs)', color: 'var(--status-down)', overflowWrap: 'anywhere' }}>{error}</span> : null}
                  </span>
                </Row>
              );
            })}
          </Rows>
        </div>
      ) : null}
    </div>
  );
}

function BuildDetail({ d }: { d: Obj }) {
  const ok = bool(d.ok);
  const node = str(d.node);
  const source = str(d.node_source);
  const seconds = num(d.seconds);
  const log = arr(d.log_tail).map((l) => (typeof l === 'string' ? l : l === null || l === undefined ? '' : String(l)));
  const took = seconds === null ? null : seconds < 90 ? `${Math.round(seconds)}s` : `${Math.floor(seconds / 60)}m ${String(Math.round(seconds % 60)).padStart(2, '0')}s`;
  const withNode = node ? ` with Node ${node}${source ? (source === 'default' ? ' (Netlify default)' : ` (from ${source})`) : ''}` : '';
  const line =
    ok === null ? (withNode ? `Built${withNode}` : null) : ok ? `Built${took ? ` in ${took}` : ''}${withNode}` : `Build failed${took ? ` after ${took}` : ''}${withNode}`;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {line ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {ok !== null ? <Badge tone={ok ? 'up' : 'down'}>{ok ? 'Built' : 'Failed'}</Badge> : null}
          <span>{line}</span>
        </div>
      ) : null}
      <Facts
        rows={[
          ['Command', str(d.command) ? <code className="t-num" style={{ fontSize: 'var(--text-xs)' }}>{str(d.command)}</code> : null],
          ['Output', str(d.output_dir) ? <code className="t-num" style={{ fontSize: 'var(--text-xs)' }}>{str(d.output_dir)}</code> : null],
        ]}
        quiet
      />
      {ok === false && log.length ? <LogTail lines={log} /> : null}
    </div>
  );
}

/** The end of a build log, scrolled to its last line, errors in red. */
function LogTail({ lines }: { lines: string[] }) {
  const ref = useRef<HTMLPreElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [lines]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <span className="t-eyebrow">Log · last {lines.length} lines</span>
      <pre
        ref={ref}
        className="t-num"
        style={{
          margin: 0,
          maxHeight: '260px',
          overflow: 'auto',
          padding: '10px 12px',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-hairline)',
          background: 'var(--surface-raised)',
          fontSize: 'var(--text-2xs)',
          lineHeight: 1.6,
          color: 'var(--text-muted)',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
        }}
      >
        {lines.map((l, i) => (
          <span key={i} style={{ display: 'block', color: /\b(error|failed)\b|npm ERR!/i.test(l) ? 'var(--status-down)' : undefined }}>
            {l || ' '}
          </span>
        ))}
      </pre>
    </div>
  );
}

const SEVERITY: Record<string, { tone: BadgeTone; rank: number }> = {
  critical: { tone: 'down', rank: 4 },
  high: { tone: 'down', rank: 3 },
  moderate: { tone: 'warn', rank: 2 },
  medium: { tone: 'warn', rank: 2 },
  low: { tone: 'neutral', rank: 1 },
  info: { tone: 'neutral', rank: 0 },
};
const sev = (v: unknown) => SEVERITY[str(v)?.toLowerCase() ?? ''] ?? { tone: 'neutral' as BadgeTone, rank: 0 };

/** "Security updates": what Dependabot knows about the repo (setup, waiting PRs, open advisories). */
function DependabotDetail({ d }: { d: Obj }) {
  const setup = obj(d.setup);
  const repo = str(d.repo);
  const prs = arr(d.prs).map(obj).filter(Boolean) as Obj[];
  const alerts = (arr(d.alerts).map(obj).filter(Boolean) as Obj[]).sort((a, b) => sev(b.severity).rank - sev(a.severity).rank);
  const onOff = (v: unknown) => <Badge tone={v === true ? 'up' : 'warn'}>{v === true ? 'On' : 'Off'}</Badge>;
  if (!repo) return <Quiet>Deployed by upload, so there is no repo for Dependabot to watch.</Quiet>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <Rows>
        <KV label="Repo">
          <a href={`https://github.com/${repo}/security/dependabot`} target="_blank" rel="noopener noreferrer" className="hover-underline t-num" style={{ color: 'var(--text-body)' }}>
            {repo}
          </a>
        </KV>
        <KV label="Alerts">{onOff(setup?.alerts)}</KV>
        <KV label="Security update PRs">{onOff(setup?.security_updates)}</KV>
        <KV label="Version update PRs">{onOff(setup?.config)}</KV>
      </Rows>
      {prs.length ? (
        <Rows>
          {prs.map((p, i) => (
            <Row key={i} align="flex-start">
              <span style={{ width: '76px', flexShrink: 0 }}>
                <Badge tone="warn">PR #{str(p.number) ?? '?'}</Badge>
              </span>
              <a href={str(p.url) ?? '#'} target="_blank" rel="noopener noreferrer" className="hover-underline" style={{ flex: 1, minWidth: 0, color: 'var(--text-body)', overflowWrap: 'anywhere' }}>
                {str(p.title) ?? 'Dependabot update'}
              </a>
            </Row>
          ))}
        </Rows>
      ) : (
        <Quiet>No Dependabot PRs waiting.</Quiet>
      )}
      {alerts.length ? (
        <Rows>
          {alerts.slice(0, 25).map((a, i) => {
            const s = sev(a.severity);
            const fix = str(a.fix);
            return (
              <Row key={i} align="flex-start">
                <span style={{ width: '76px', flexShrink: 0 }}>
                  <Badge tone={s.tone}>{str(a.severity) ?? '—'}</Badge>
                </span>
                <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <span className="t-num" style={{ color: 'var(--text-body)', overflowWrap: 'anywhere' }}>{str(a.package) ?? 'Package'}</span>
                    {str(a.relationship) === 'direct' ? <Tag>direct</Tag> : null}
                    {str(a.scope) === 'development' ? <Tag>dev only</Tag> : null}
                    {bool(a.has_pr) ? <Tag>PR open</Tag> : null}
                  </span>
                  {str(a.summary) ? (
                    <a href={str(a.url) ?? '#'} target="_blank" rel="noopener noreferrer" className="hover-underline" style={{ color: 'var(--text-muted)', overflowWrap: 'anywhere' }}>
                      {str(a.summary)}
                    </a>
                  ) : null}
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                    {fix ? (
                      <>
                        fixed in <span className="t-num">{fix}</span>
                      </>
                    ) : (
                      'no fix released yet; Dependabot opens a PR when one ships'
                    )}
                  </span>
                </span>
              </Row>
            );
          })}
        </Rows>
      ) : (
        <Quiet>No open advisories.</Quiet>
      )}
    </div>
  );
}

function AuditDetail({ d }: { d: Obj }) {
  if (d.source === 'dependabot') return <DependabotDetail d={d} />;
  const counts = obj(d.counts);
  const scope = str(d.scope);
  const advisories = (arr(d.advisories).map(obj).filter(Boolean) as Obj[]).sort((a, b) => sev(b.severity).rank - sev(a.severity).rank);
  const total = counts ? (['critical', 'high', 'moderate', 'low'] as const).reduce((n, k) => n + (num(counts[k]) ?? 0), 0) : null;
  if (!counts && !advisories.length) return <RawDetail d={d} />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {scope ? <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>{words(scope)} dependencies only.</span> : null}
      {counts ? (
        // Four across on a wide card, two by two on a phone.
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))', gap: '6px', maxWidth: '520px' }}>
          {(
            [
              ['critical', 'down'],
              ['high', 'down'],
              ['moderate', 'warn'],
              ['low', 'neutral'],
            ] as const
          ).map(([k, tone]) => (
            <CountChip key={k} n={num(counts[k])} label={words(k)} tone={tone} />
          ))}
        </div>
      ) : null}
      {advisories.length ? (
        <Rows>
          {advisories.map((a, i) => {
            const s = sev(a.severity);
            const url = str(a.url);
            const title = str(a.title);
            const fix = typeof a.fix === 'boolean' ? (a.fix ? 'available' : 'none available') : str(a.fix);
            return (
              <Row key={i} align="flex-start">
                <span style={{ width: '76px', flexShrink: 0 }}>
                  <Badge tone={s.tone}>{str(a.severity) ?? '—'}</Badge>
                </span>
                <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <span className="t-num" style={{ color: 'var(--text-body)', overflowWrap: 'anywhere' }}>
                      {str(a.name) ?? 'Package'}
                    </span>
                    {bool(a.direct) ? <Tag>direct</Tag> : null}
                  </span>
                  {title ? (
                    url ? (
                      <a href={url} target="_blank" rel="noopener noreferrer" className="hover-underline" style={{ color: 'var(--text-muted)', overflowWrap: 'anywhere' }}>
                        {title}
                      </a>
                    ) : (
                      <span style={{ color: 'var(--text-muted)', overflowWrap: 'anywhere' }}>{title}</span>
                    )
                  ) : null}
                  {fix ? (
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', overflowWrap: 'anywhere' }}>
                      fix: <span className="t-num">{fix}</span>
                    </span>
                  ) : null}
                </span>
              </Row>
            );
          })}
        </Rows>
      ) : total === 0 ? (
        <Quiet>No known vulnerabilities.</Quiet>
      ) : null}
    </div>
  );
}

const NODE_STATUS: Record<string, { tone: BadgeTone; label: string }> = {
  ok: { tone: 'up', label: 'Supported' },
  eol: { tone: 'down', label: 'End of life' },
  unpinned: { tone: 'warn', label: 'Not pinned' },
};

function VersionsDetail({ d }: { d: Obj }) {
  const node = obj(d.node);
  const packages = arr(d.packages).map(obj).filter(Boolean) as Obj[];
  const status = str(node?.status);
  const look = status ? NODE_STATUS[status] ?? { tone: 'neutral' as BadgeTone, label: words(status) } : null;
  const eol = str(node?.eol);
  const eolPast = eol ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(eol) ? `${eol}T12:00:00` : eol).getTime() < Date.now() : false;
  const source = str(node?.source);
  const pinned = bool(node?.pinned);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <Facts
        rows={[
          [
            'Node',
            node ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px 8px', flexWrap: 'wrap' }}>
                {str(node.version) ? <span className="t-num">{str(node.version)}</span> : null}
                {look ? <Badge tone={look.tone}>{look.label}</Badge> : null}
                {source || (pinned === false && status !== 'unpinned') ? (
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>{source ? `from ${source}` : 'not pinned in the repo'}</span>
                ) : null}
              </span>
            ) : null,
          ],
          ['End of life', eol ? <span style={{ color: eolPast ? 'var(--status-down)' : undefined }}>{eolPast ? `Since ${day(eol)}` : day(eol)}</span> : null],
          ['Functions', str(d.functions_runtime) ? <span className="t-num">{str(d.functions_runtime)}</span> : null],
        ]}
      />
      {packages.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <span className="t-eyebrow">Packages · {packages.length}</span>
          <Rows>
            {packages.map((p, i) => {
              const behind = num(p.majors_behind);
              return (
                <div key={i} style={{ padding: '7px 0', borderTop: '1px solid var(--border-hairline)', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <span className="t-num" style={{ flex: '1 1 140px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-body)' }}>
                    {str(p.name) ?? 'Package'}
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', marginLeft: 'auto' }}>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                      <FromTo from={p.installed} to={p.latest} />
                    </span>
                    {behind === null ? null : behind <= 0 ? (
                      <Badge tone="up">Current</Badge>
                    ) : (
                      <Badge tone={behind >= 2 ? 'down' : 'warn'}>
                        {behind} major{behind === 1 ? '' : 's'} behind
                      </Badge>
                    )}
                  </span>
                </div>
              );
            })}
          </Rows>
        </div>
      ) : null}
    </div>
  );
}

function SecretsDetail({ d }: { d: Obj }) {
  const scanned = obj(d.scanned);
  const pages = num(scanned?.pages);
  const scripts = num(scanned?.scripts);
  const size = bytes(num(scanned?.bytes));
  const findings = (arr(d.findings).map(obj).filter(Boolean) as Obj[]).sort((a, b) => sev(b.severity).rank - sev(a.severity).rank);
  const parts = [pages !== null ? `${pages} page${pages === 1 ? '' : 's'}` : null, scripts !== null ? `${scripts} script${scripts === 1 ? '' : 's'}` : null].filter(Boolean);
  if (!scanned && !Array.isArray(d.findings)) return <RawDetail d={d} />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {parts.length || size ? (
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
          Scanned {parts.join(', ') || 'the bundle'}
          {size ? ` (${size})` : ''}.
        </span>
      ) : null}
      {findings.length ? (
        <Rows>
          {findings.map((f, i) => {
            const where = str(f.where);
            const sample = str(f.sample);
            return (
              <Row key={i} align="flex-start">
                <span style={{ width: '76px', flexShrink: 0 }}>
                  <Badge tone={sev(f.severity).tone}>{str(f.severity) ?? '—'}</Badge>
                </span>
                <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span style={{ color: 'var(--text-body)' }}>{str(f.kind) ? words(str(f.kind) as string) : 'Possible secret'}</span>
                  {sample ? (
                    <code
                      className="t-num"
                      style={{
                        alignSelf: 'flex-start',
                        maxWidth: '100%',
                        padding: '2px 6px',
                        borderRadius: 'var(--radius-xs)',
                        background: 'var(--surface-raised)',
                        fontSize: 'var(--text-xs)',
                        color: 'var(--text-muted)',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {sample}
                    </code>
                  ) : null}
                  {where ? (
                    /^https?:\/\//i.test(where) ? (
                      <ExtLink href={where} small>
                        {host(where)}
                      </ExtLink>
                    ) : (
                      <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', overflowWrap: 'anywhere' }}>
                        {where}
                      </span>
                    )
                  ) : null}
                </span>
              </Row>
            );
          })}
        </Rows>
      ) : Array.isArray(d.findings) ? (
        <Quiet>Nothing secret-looking in the public bundle.</Quiet>
      ) : null}
    </div>
  );
}

const HEADER_NAMES: Record<string, string> = {
  hsts: 'Strict-Transport-Security',
  x_content_type_options: 'X-Content-Type-Options',
  x_frame_options: 'X-Frame-Options',
  referrer_policy: 'Referrer-Policy',
  content_security_policy: 'Content-Security-Policy',
  permissions_policy: 'Permissions-Policy',
};
const headerKey = (s: string) => s.trim().toLowerCase().replace(/-/g, '_');

function HeadersDetail({ d }: { d: Obj }) {
  const cert = obj(d.cert);
  const headers = obj(d.headers);
  const missing = new Set((arr(d.missing).map(str).filter(Boolean) as string[]).map(headerKey));
  const yesNo = (v: boolean | null, good = true) => (v === null ? null : <Badge tone={v === good ? 'up' : 'down'}>{v ? 'Yes' : 'No'}</Badge>);
  const daysLeft = num(cert?.days_left);
  const validTo = str(cert?.valid_to);
  const issuer = str(cert?.issuer);
  const keys = [...new Set([...Object.keys(HEADER_NAMES), ...Object.keys(headers ?? {})])].filter(
    (k) => headers?.[k] !== undefined || missing.has(k) || missing.has(headerKey(HEADER_NAMES[k] ?? k)),
  );
  return (
    <Rows>
      <KV label="Served by Netlify">{yesNo(bool(d.serves_netlify))}</KV>
      <KV label="HTTP → HTTPS redirect">{yesNo(bool(d.https_redirect))}</KV>
      <KV label="Certificate">
        {cert ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px 8px', flexWrap: 'wrap' }}>
            {validTo ? <span>Expires {day(validTo)}</span> : null}
            {daysLeft !== null ? (
              <Badge tone={daysLeft < 7 ? 'down' : daysLeft < 21 ? 'warn' : 'up'}>
                {daysLeft < 0 ? 'Expired' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}
              </Badge>
            ) : null}
            {issuer ? <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>{issuer}</span> : null}
          </span>
        ) : null}
      </KV>
      {keys.map((k) => {
        const name = HEADER_NAMES[k] ?? words(k);
        const value = str(headers?.[k]);
        return (
          <KV key={k} label={name} mono>
            {value && !missing.has(k) ? (
              <span
                className="t-num"
                title={value}
                style={{ display: 'block', fontSize: 'var(--text-xs)', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {value}
              </span>
            ) : (
              <Badge tone="warn">Missing</Badge>
            )}
          </KV>
        );
      })}
    </Rows>
  );
}

/** Unknown step types, and known ones whose detail didn't match: key/value. */
function RawDetail({ d }: { d: Obj }) {
  const entries = Object.entries(d);
  if (!entries.length) return <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>No detail recorded.</span>;
  return (
    <Facts
      rows={entries.map(([k, v]) => [
        words(k),
        v === null || v === undefined ? (
          '—'
        ) : typeof v === 'object' ? (
          <code className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', overflowWrap: 'anywhere' }}>
            {JSON.stringify(v).slice(0, 400)}
          </code>
        ) : (
          String(v)
        ),
      ])}
    />
  );
}

// ───────────────────────── Pieces ─────────────────────────

function FromTo({ from, to }: { from: unknown; to: unknown }) {
  return (
    <span className="t-num" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
      {str(from) ?? '?'}
      <Icon name="arrow-right" size={11} style={{ color: 'var(--text-faint)' }} />
      <span style={{ color: 'var(--text-body)' }}>{str(to) ?? '?'}</span>
    </span>
  );
}

/** `quiet`: render nothing when every row is empty, for facts that sit under other content. */
function Facts({ rows, quiet }: { rows: Array<[string, React.ReactNode]>; quiet?: boolean }) {
  const shown = rows.filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (!shown.length) return quiet ? null : <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>No detail recorded.</span>;
  return (
    <dl style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {shown.map(([label, value]) => (
        <div key={label} style={{ display: 'grid', gridTemplateColumns: '104px 1fr', gap: '12px', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
          <dt className="t-eyebrow" style={{ paddingTop: '3px' }}>
            {label}
          </dt>
          <dd style={{ margin: 0, minWidth: 0, overflowWrap: 'anywhere' }}>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** A label/value row that stacks on a phone; for labels too long for Facts' column. */
function KV({ label, mono, children }: { label: string; mono?: boolean; children: React.ReactNode }) {
  if (children === null || children === undefined || children === false) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px 12px', flexWrap: 'wrap', padding: '7px 0', borderTop: '1px solid var(--border-hairline)' }}>
      <span className={mono ? 't-num' : undefined} style={{ flex: '0 0 200px', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
        {label}
      </span>
      <span style={{ flex: '1 1 160px', minWidth: 0, minHeight: '20px', display: 'flex', alignItems: 'center' }}>{children}</span>
    </div>
  );
}

function CountChip({ n, label, tone }: { n: number | null; label: string; tone: 'down' | 'warn' | 'neutral' }) {
  const zero = !n;
  const color = zero ? 'var(--text-faint)' : tone === 'neutral' ? 'var(--text-body)' : `var(--status-${tone})`;
  const bg = zero || tone === 'neutral' ? 'var(--surface-raised)' : `var(--status-${tone}-soft)`;
  return (
    <span style={{ display: 'flex', alignItems: 'baseline', gap: '6px', padding: '4px 10px', borderRadius: 'var(--radius-md)', background: bg }}>
      <span className="t-num" style={{ fontSize: 'var(--text-base)', fontWeight: 600, color }}>
        {n ?? '—'}
      </span>
      <span style={{ fontSize: 'var(--text-xs)', color: zero ? 'var(--text-faint)' : 'var(--text-muted)' }}>{label}</span>
    </span>
  );
}

function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="t-num"
      style={{ padding: '0 5px', borderRadius: 'var(--radius-xs)', border: '1px solid var(--border-hairline)', fontSize: 'var(--text-2xs)', lineHeight: '15px', color: 'var(--text-faint)' }}
    >
      {children}
    </span>
  );
}

function Quiet({ children }: { children: React.ReactNode }) {
  return <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>{children}</span>;
}

function ExtLink({ href, small, children }: { href: string; small?: boolean; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="hover-underline"
      style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', minWidth: 0, maxWidth: '100%', fontSize: small ? 'var(--text-xs)' : undefined, color: 'var(--text-muted)' }}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{children}</span>
      <Icon name="external-link" size={11} style={{ flexShrink: 0, color: 'var(--text-faint)' }} />
    </a>
  );
}

function Commit({ sha, url }: { sha: string; url: string | null }) {
  const short = /^[0-9a-f]{8,}$/i.test(sha) ? sha.slice(0, 7) : sha;
  return url ? (
    <a href={url} target="_blank" rel="noopener noreferrer" className="t-num hover-underline" title={sha} style={{ color: 'var(--text-muted)' }}>
      {short}
    </a>
  ) : (
    <span className="t-num" title={sha} style={{ color: 'var(--text-muted)' }}>
      {short}
    </span>
  );
}

const shortDay = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

function Rows({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'flex', flexDirection: 'column', fontSize: 'var(--text-sm)' }}>{children}</div>;
}

function Row({ children, align = 'center' }: { children: React.ReactNode; align?: 'center' | 'flex-start' }) {
  return <div style={{ display: 'flex', alignItems: align, gap: '12px', padding: '7px 0', borderTop: '1px solid var(--border-hairline)' }}>{children}</div>;
}

function Note({ tone, children }: { tone: 'warn' | 'down'; children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: '8px 10px',
        borderRadius: 'var(--radius-md)',
        background: tone === 'warn' ? 'var(--status-warn-soft)' : 'var(--status-down-soft)',
        color: tone === 'warn' ? 'var(--status-warn)' : 'var(--status-down)',
        fontSize: 'var(--text-xs)',
        lineHeight: 'var(--leading-snug)',
      }}
    >
      {children}
    </div>
  );
}
